import React, { useEffect, useState } from "react";
import { Button, Grid, LinearProgress, Typography } from "@material-ui/core";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import { GridTextField } from "components";
import { useSnackbar } from "notistack";
import { Club } from "api/clubs";
import { CompanySettings, getCompanySettings, getCompanySubscriptons, Subscription, updateCompanySettings } from "api/company";
import i18n from "./i18n";
import "./b2bfeaturesStyle.scss";

interface CustomerDetailsProps {
    club: Club,
}

const CustomerDetails = ({ club }: CustomerDetailsProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const [isLoading, setLoading] = useState(false);
    const [companySettings, setCompanySettings] = useState<CompanySettings>();

    const fetchCompanySettings = async (clubId: string): Promise<void> => {
        try {
            setLoading(true);
            try {
                if (club.clubStatus !== "demo") {
                    const subscriptions: Subscription[] = await getCompanySubscriptons({ clubId });
                    if (subscriptions.length > 0) {
                        setCompanySettings(await getCompanySettings({ clubId }));
                    }
                }
            } catch (e) {
                displayError(e);
            }
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };

    const saveInvoiceSettings = () => {
        try {
            setLoading(true);
            updateCompanySettings({ clubId: club.id, changes: { customer: { name: companySettings?.customer.name, address: companySettings?.customer.address, email: companySettings?.customer.email } } });
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (club) {
            fetchCompanySettings(club.id);
        }
    }, [club]);

    return (
        <>
            <Grid container spacing={2}>
                {companySettings && (
                    <>
                        <Grid item xs={12} md={12}>&nbsp;</Grid>
                        <Grid item xs={12} md={12}>
                            <Typography> </Typography>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Typography align="left" variant="h6">{i18n.invoiceSettings}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {isLoading && <LinearProgress />}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.companyName}
                                size={12}
                                value={companySettings.customer.name || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, name: v } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.addressLine1}
                                size={12}
                                value={companySettings.customer.address?.line1 || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, address: { ...companySettings.customer.address!, line1: v } } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.addressLine2}
                                size={12}
                                value={companySettings.customer.address?.line2 || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, address: { ...companySettings.customer.address!, line2: v } } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.city}
                                size={12}
                                value={companySettings.customer.address?.city || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, address: { ...companySettings.customer.address!, city: v } } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.state}
                                size={12}
                                value={companySettings.customer.address?.state || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, address: { ...companySettings.customer.address!, state: v } } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.postCode}
                                size={12}
                                value={companySettings.customer.address?.postal_code || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, address: { ...companySettings.customer.address!, postal_code: v } } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.country}
                                size={12}
                                value={companySettings.customer.address?.country || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, address: { ...companySettings.customer.address!, country: v } } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <CurrencyTextField
                                currencySymbol="€"
                                decimalCharacter=","
                                digitGroupSeparator="."
                                fullWidth
                                label={i18n.upComing}
                                disabled
                                helperText=""
                                maximumValue="1000000"
                                minimumValue="-100000"
                                value={companySettings.customer.balance ? companySettings.customer.balance / 100 : 0}
                                variant="outlined"
                                onChange={(e: any, value: number) => setCompanySettings({ customer: { ...companySettings.customer, balance: value } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <GridTextField
                                label={i18n.email}
                                size={12}
                                value={companySettings.customer.email || ""}
                                onChange={(v) => setCompanySettings({ customer: { ...companySettings.customer, email: v } })}
                            />
                        </Grid>
                        <Grid item xs={12} md={12} className="settingBtn">
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={saveInvoiceSettings}
                            >
                                {i18n.invoiceSettingsButton}
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>
        </>
    );
};

export default CustomerDetails;
